/**
 * The timestamps a Waper writes under a shared video are its chapters ("Bouteille de Whisky #1 - Étrange
 * Whisky: 0:00", "0:06 Porte Cauchemardesque #1", "1:11 - Caisse de matériaux"). They are read from the
 * text for the structured data (schema.org Clip, the "key moments" of Google and the assistants) and
 * turned into links on the HTML, so that a click starts the video at that moment (useVideoSeek).
 */
export type VideoChapter = { start: number; name: string }

// h:mm:ss or m:ss, not glued to other digits (so "2:35" in "12:35" or a score "10-2:35" is left alone)
const TIME = /(?<![\d:])(?:(\d{1,2}):)?(\d{1,2}):(\d{2})(?![\d:])/g
// what separates the time from its label: "Nom: 0:06", "0:06 - Nom", "0:06 – Nom", "0:06 | Nom", "[0:06] Nom"
const SEPARATORS = /^[\s\-–—:|.()[\]]+|[\s\-–—:|.()[\]]+$/g

export const toSeconds = (h?: string, m?: string, s?: string) =>
  (h ? Number(h) * 3600 : 0) + (m ? Number(m) * 60 : 0) + (s ? Number(s) : 0)

/** ISO 8601 duration of an offset in seconds, as schema.org Clip.startOffset accepts it: 66 → PT1M6S */
export const isoDuration = (seconds: number) => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${s || (!h && !m) ? `${s}S` : ''}`
}

/** The lines of an HTML fragment as plain text (one per paragraph, list item or line break). */
export const plainTextLines = (html?: string | null) =>
  (html ?? '')
    .replace(/<\/(p|div|li|h[1-6]|tr)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)

/**
 * The chapters of a video described by a text: one timestamp per line with a label. A single timestamp is a
 * mention, not a chapter list; chapters out of order or repeated are dropped (the text is a Waper's, not a
 * table of contents).
 */
export const parseVideoChapters = (html?: string | null): VideoChapter[] => {
  const chapters: VideoChapter[] = []

  for (const line of plainTextLines(html)) {
    const times = [...line.matchAll(TIME)]
    // a line with several timestamps is a sentence ("de 1:10 à 2:30"), not a chapter
    if (times.length !== 1) continue

    const [match, h, m, s] = times[0]
    const name = line.replace(match, ' ').replace(SEPARATORS, '').replace(/\s+/g, ' ').trim()
    const start = toSeconds(h, m, s)
    const last = chapters[chapters.length - 1]
    if (!name || name.length > 120 || (last && start <= last.start)) continue

    chapters.push({ start, name })
  }

  return chapters.length >= 2 ? chapters : []
}

/**
 * Turns the timestamps of a sanitized HTML fragment into chapter links ("?t=66"): only in the text, never
 * inside a tag or an existing link. Runs after sanitizeUserHtml: the markup added here is ours.
 */
export const linkifyVideoChapters = (html?: string | null) => {
  if (!html || !parseVideoChapters(html).length) return html ?? ''

  let inLink = 0
  return html
    .split(/(<[^>]+>)/)
    .map((token) => {
      if (token.startsWith('<')) {
        if (/^<a[\s>]/i.test(token)) inLink++
        else if (/^<\/a\s*>/i.test(token)) inLink = Math.max(0, inLink - 1)
        return token
      }
      if (inLink) return token
      return token.replace(TIME, (match, h?: string, m?: string, s?: string) => {
        const seconds = toSeconds(h, m, s)
        return `<a class="yt-chapter" href="?t=${seconds}" data-seek="${seconds}">${match}</a>`
      })
    })
    .join('')
}

/** The start offset asked by a chapter link or a "?t=66" page address, in seconds. */
export const startOffset = (value: unknown) => {
  const seconds = Number(Array.isArray(value) ? value[0] : value)
  return Number.isInteger(seconds) && seconds >= 0 && seconds < 86400 ? seconds : undefined
}
